/* eslint-disable react/display-name */
import React, { memo, useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { dataLayer, utilities } from '@weatherbug/common';
import { reduxConnect } from '../../common';
import { smallMediaQuery, mediumMediaQuery } from '../../common/utilities/sizeHelpers';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { AirQuality, IAppState, IStoreState } from '../common/interfaces/types';
import { AirQualityList } from './airQualityList';
import { AlertsMap } from '../alerts/alertsMap';
import { DisplayAdWidget } from '../ads/displayAdWidget.jsx';
import { translate } from '../../common/translation/translate';
import { AirQualitySummaryArticleSection } from './v2/airQualitySummaryArticleSection';
import { AqiTopTriggerHealthAdvicePopup } from './v2/aqiHealthAdviceTopTriggerDetailsModal';
import { ErrorBoundary } from '../common/errorBoundary/errorBoundary';
import { aqiNoDataErrorStatusCode } from './constants';
import { NoAqiDataUI } from './v2/noDataUI/noAqiDataUI';
import { BotMap } from '../common/botMapView/botMapView';
import { DisplayAdUnit } from '../../containers/ads/displayAdUnitContainer';

const { getCurrentAirQualityByLatLon } = dataLayer.actions;

const { unitSystemsEnum } = utilities.units;
const activePageLayerId = 'AirQuality';

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState, units, aqiTopTriggerHealthAdviceDetailModal, airQuality } = storeState;
    const { 
        aqiBaseUrl, 
        appVersion, 
        authId, 
        os, 
        osVersion, 
        pageId, 
        secret, 
        stdParameters, 
        userLocale,
        mapboxAccessToken,
        mapBaseUrl,
        tileBaseUrl,
        tempUnit,
        user,
        airQualityAttributionUrl
    } = appState?.appState as IAppState ?? {};  
    const { isTopTriggerHealthAdviceDetailPopupOpen } = aqiTopTriggerHealthAdviceDetailModal ?? {}; 
    const locality = user?.ActiveLocation?.Locality;
    const routeString = `/${locality?.RouteString}`;

    const { IsBot } = user || {};
    
    const { unitSystem } = units ?? {};

    const { lastError = {} } = airQuality as AirQuality;
    const isNoDataError = lastError?.responseStatus === aqiNoDataErrorStatusCode;

    return {
        isNoDataError,
        isTopTriggerHealthAdviceDetailPopupOpen,
        activePageId: pageId,
        aqiBaseUrl,
        appVersion,
        authId,
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        locationName: locality?.DisplayCompositeName,
        routeString,
        os,
        osVersion,
        secret,
        stdParameters,
        userLocale,
        mapboxAccessToken,
        mapBaseUrl,
        tileBaseUrl,
        tempUnit,
        unitSystem: unitSystemsEnum[unitSystem as keyof typeof unitSystemsEnum],
        airQualityAttributionUrl,
        isBot: IsBot
    };
};

const mapDispatchToProps = { getCurrentAirQualityByLatLon };

interface AirQualityPageProps {
    activePageId: string;
    aqiBaseUrl: string;
    authId: string;
    latitude: number;
    locationName: string;
    longitude: number;
    routeString: string;
    secret: string;
    stdParameters: Record<string, unknown>;
    mapboxAccessToken: string;
    mapBaseUrl: string;
    tileBaseUrl: string;
    tempUnit: number;
    pressureUnit: number;
    rainUnit: number;
    windUnit: number;
    unitSystem: string;
    airQualityAttributionUrl: string;
    isNoDataError: boolean;
    isTopTriggerHealthAdviceDetailPopupOpen: boolean;
    getCurrentAirQualityByLatLon: typeof dataLayer.actions.getCurrentAirQualityByLatLon;
    isTablet: boolean;
    isMobile: boolean;
    isBot: boolean;
}

const AirQualityPage = withScreenSize(reduxConnect(mapStoreStateToProps, mapDispatchToProps) (memo(({
    activePageId,
    aqiBaseUrl,
    authId,
    latitude,
    locationName,
    longitude,
    secret,
    stdParameters,
    mapboxAccessToken,
    mapBaseUrl,
    tileBaseUrl,
    tempUnit,
    pressureUnit,
    rainUnit,
    windUnit,
    airQualityAttributionUrl,
    isTopTriggerHealthAdviceDetailPopupOpen,
    getCurrentAirQualityByLatLon,
    isMobile,
    isTablet,
    isBot,
    isNoDataError }: AirQualityPageProps) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const prevLatLng = useRef<{ latitude: number | null; longitude: number | null; }>({
        latitude: null,
        longitude: null
    });

    const fetchAirQuality = useCallback(async(aqiBaseUrl, authId, latitude, longitude, secret) => {
        if (prevLatLng.current?.latitude !== latitude || prevLatLng.current?.longitude !== longitude) {
            prevLatLng.current = { latitude, longitude };
            try {
                if (aqiBaseUrl && authId && !Number.isNaN(latitude) && !Number.isNaN(longitude) && secret) {
                    await getCurrentAirQualityByLatLon(latitude, longitude, false, aqiBaseUrl, authId, secret, {}, true);
                }
            } catch (error) {
                console.log(error);
            }
        }
    }, []);

    useEffect(() => {
        fetchAirQuality(aqiBaseUrl, authId, latitude, longitude, secret);
    }, [aqiBaseUrl, authId, latitude, longitude, secret, stdParameters]);
    
    return (
        <Container id='com.weatherbug.web:id/airQualityPage'>
            {( isTablet || isMobile) 
                && <MobileBannerAd>
                    <DisplayAdUnit
                        width={isMobile ? 320 : 728}
                        height={isMobile ? 50 : 90}
                        slotId={isMobile ? 'air-quality_header_x83' : 'wb_banner'}
                    />
                </MobileBannerAd>
            }
            <HeaderTextContainer>
                <HeaderText>
                    {`${translate('airquality')} - ${locationName}`}
                </HeaderText>
            </HeaderTextContainer>
            <Content>
                <AirQualityList
                    activePageId={activePageId}
                />
                {
                    isNoDataError
                        ? <NoAqiDataUI isMap={true} />
                        : <ErrorBoundary customStyle={ErrorBoundaryStyle} errorDescription={translate('dataLoadErrorMessage')}>
                            <AlertsMap
                                isAirQualityPage={activePageId} 
                                layerId={activePageLayerId}
                                latitude={latitude}
                                longitude={longitude}
                                mapboxAccessToken={mapboxAccessToken}
                                mapBaseUrl={mapBaseUrl}
                                tileBaseUrl={tileBaseUrl}
                                tempUnit={tempUnit}
                                pressureUnit={pressureUnit}
                                rainUnit={rainUnit}
                                windUnit={windUnit}
                                showAttributionIcon={false}
                                authId={authId}
                                secret={secret}
                                stdParameters={stdParameters}
                                isBot={isBot}
                                airQualityAttributionUrl={airQualityAttributionUrl} />
                            
                        </ErrorBoundary>
                }
            </Content>
            <AirQualitySummaryArticleSection />
            {   isTopTriggerHealthAdviceDetailPopupOpen
                && <AqiTopTriggerHealthAdvicePopup 
                    isTopTriggerHealthAdviceDetailPopupOpen={isTopTriggerHealthAdviceDetailPopupOpen} />
            }
        </Container>
    );
})));

export { AirQualityPage as default };

const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-width: calc(1218px + 8%);
    padding-left: 4%;
    padding-right: 4%;
    margin: 0 auto;

    @media ${mediumMediaQuery} {
        width: 100%;
        margin-left: auto;
        margin-right: auto;
        padding-left: 15px;
        padding-right: 15px;
    }

    @media ${smallMediaQuery} {
        width: 100%;
        padding-left: 10px;
        padding-right: 10px;
        margin-left: auto;
        margin-right: auto;
    }
`; 

const MobileBannerAd = styled.div`
    margin-bottom: 16px;
`;

const Content = styled.div`
    display: flex;
    flex-direction: row;

    @media ${smallMediaQuery} {
        flex-flow: column wrap;
    }
`;

const HeaderTextContainer = styled.header`
    flex-direction: row;
    flex: 0 0 100%;
`;

const HeaderText = styled.h1`
    text-align: left;
    color: #ffffff;
    background-color: transparent;
    font-size: 22px;
    font-family: Source Sans Pro, sans-serif;
    height: 32px;
    
    @media ${smallMediaQuery} {
        font-size: 19px;
    }
`;

const ErrorBoundaryStyle = css`
    display: flex;
    flex-direction: column;
    width: 72%;
    margin-left: 10px;
    padding-right: 0;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    height: 600px;

    @media ${mediumMediaQuery} {
        width: 52%;
    }

    @media ${smallMediaQuery} {
        margin: 10px 0 0 0;
        height: 400px;
        width: 100%;
    }
`;
